import React, { useEffect } from "react";
import {
  EuroIcon,
  Briefcase,
  TrendingUp,
  Shield,
  Users,
  Compass,
} from "lucide-react";

import { ProjectHero } from "../ui/ProjectHero/ProjectHero";
import { VisualShowcase } from "../ui/VisualShowcase/VisualShowcase";
import { ProjectOverview } from "../ui/ProjectOverview/ProjectOverview";
import { BulletItem, BulletList } from "../ui/Bullets/Bullets";
import { KeyFeatures } from "../ui/KeyFeatures/KeyFeatures";
import { ProcessSection } from "../ui/ProcessSection/ProcessSection";
import { ProjectFooter } from "../ui/ProjectFooter/ProjectFooter";
import Footer from "../../../container/InlineFooter/InlineFooter";

import images from "../../../constants/images";

import "./CodamDevelopment.scss";

const CodamDevelopment = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  const bulletListLeft = (
    <BulletList>
      <BulletItem>Navigating interviews and hiring processes</BulletItem>
      <BulletItem>Understanding the job market and its dynamics</BulletItem>
      <BulletItem>
        Balancing technical growth with business and product thinking
      </BulletItem>
      <BulletItem>
        Managing confidence, imposter syndrome, and career direction
      </BulletItem>
      <BulletItem>
        Choosing between freelancing, contracting, or full-time roles
      </BulletItem>
    </BulletList>
  );

  return (
    <div className="main-project-container">
      <ProjectHero
        title="Coaching Students on Professional Development"
        subtitle="Preparing the next generation of software engineers to navigate real-world careers with confidence, clarity, and commercial awareness."
        client="Codam Coding College"
        year="2023"
        role="Professional Development Coach & Technical Mentor"
        platform="Education / In-Person Workshop"
        technologies={["Web Engineering, AI Engineering"]}
        category="MENTORING"
      />
      <VisualShowcase
        imageUrl={images.codamV3Img}
        imageAlt="Codam birds-eye view"
        layout="full"
      />
      <ProjectOverview
        challengeContent={[
          "Bridging the gap between technical education and professional reality",
          "While Codam students develop strong technical foundations, many face uncertainty when transitioning into industry. Common challenges included:",
        ]}
        bulletsLeft={bulletListLeft}
        extraChallengeContent={[
          "The challenge was not to teach more code — but to help students understand how software engineering operates as a profession and a market.",
        ]}
        solutionContent={[
          "Practical, experience-driven professional development coaching",
          "I was invited back to Codam in February 2023 to deliver a professional development workshop and mentorship programme grounded in real industry experience.",
          "The approach combined group sessions with focused one-to-one mentoring, covering both technical growth paths and the commercial realities of working as a software engineer. The goal was to equip students with frameworks, confidence, and decision-making tools they could carry into any role or organisation.",
        ]}
        metrics={[
          {
            value: "Codam cohort",
            label:
              "Professional development workshop delivered to active students",
          },
          {
            value: "Career-focused",
            label: "Guidance aligned with real-world engineering roles",
          },
          {
            value: "Multi-dimensional",
            label:
              "Covered technical, business, and professional growth topics",
          },
          {
            value: "Market-aware",
            label: "Insights grounded in current industry conditions",
          },
        ]}
      />
      <VisualShowcase
        layout="full"
        title="Professional Development Workshop at Codam"
        description="A look into the in-person workshop environment, discussing real-world software engineering careers, team dynamics, and professional growth with Codam students."
        backgroundColor="black"
        descriptionColor="#9ca3af"
        titleColor="#ffffff"
        imageUrl={images.codamV1Img}
        imageAlt="Codam workshop"
      />
      <KeyFeatures
        title="Professional Development in Practice"
        description="Core topics designed to prepare students for long-term success in the software industry."
        features={[
          {
            icon: Briefcase,
            title: "Interview preparation & positioning",
            description:
              "Helping students articulate their skills, projects, and value clearly during interviews.",
          },
          {
            icon: TrendingUp,
            title: "Understanding the job market",
            description:
              "Exploring market dynamics, demand trends, and how engineers create value.",
          },
          {
            icon: Shield,
            title: "Confidence & imposter syndrome",
            description:
              "Reframing imposter syndrome as a growth signal rather than a weakness.",
          },
          {
            icon: Users,
            title: "Thriving within teams",
            description:
              "Navigating collaboration, communication, and organisational dynamics.",
          },
          {
            icon: EuroIcon,
            title: "Contracts, salary & freelancing",
            description:
              "Understanding compensation, negotiation, and employment models.",
          },
          {
            icon: Compass,
            title: "Long-term career strategy",
            description:
              "Building adaptable careers focused on continuous learning and relevance.",
          },
        ]}
      />
      <ProcessSection
        title="Coaching for Real-World Software Careers"
        description="A practical, experience-led approach to preparing students for life beyond formal education."
        phases={[
          {
            number: "01",
            title: "Context & Career Framing",
            description:
              "Established a shared understanding of how software engineering operates as both a discipline and a profession.",
            deliverables: [
              "Career landscape overview",
              "Industry expectations",
              "Common early-career pitfalls",
              "Mindset alignment",
            ],
          },
          {
            number: "02",
            title: "Individual Mentorship",
            description:
              "Worked one-to-one with students to align technical interests with realistic career paths.",
            deliverables: [
              "Personal development focus areas",
              "Technical growth guidance",
              "Career direction coaching",
              "Confidence building",
            ],
          },
          {
            number: "03",
            title: "Professional Readiness",
            description:
              "Prepared students for interviews, negotiations, and real-world team environments.",
            deliverables: [
              "Interview preparation",
              "CV and portfolio positioning",
              "Communication coaching",
              "Market awareness",
            ],
          },
          {
            number: "04",
            title: "Sustainable Growth Mindset",
            description:
              "Focused on long-term adaptability rather than short-term role placement.",
            deliverables: [
              "Learning strategies",
              "Skill maintenance frameworks",
              "Business awareness",
              "Career resilience",
            ],
          },
        ]}
      />
      <ProjectFooter
        impactTitle="Empowering students to navigate software careers with confidence"
        impactLabel="Project Impact"
        impactDescription="The programme helped students contextualise their technical skills within the broader software industry. By combining mentorship, real-world insights, and professional development coaching, students gained clarity on how to position themselves, grow sustainably, and thrive across different roles and industries."
        nextProject={{
          title: "Codam Student Mentoring Programme",
          link: "/project/codam-mentoring",
        }}
        conclusion={{
          label: "Design / Engineering Reflection",
          title:
            "Great engineers are built through perspective, not just practice",
          description:
            "This project reinforced that technical ability alone is not enough to sustain a long-term career. Engineers who understand business context, team dynamics, and their own value are better equipped to adapt, contribute meaningfully, and grow — regardless of the tools or technologies they work with.",
        }}
      />
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Footer />
      </div>
    </div>
  );
};

export { CodamDevelopment };

/*
Project Context

Codam is a tuition-free software engineering educator with a peer-to-peer learning model that removes traditional teachers and lectures. Its curriculum is fully project-based, collaborative, and designed to mirror professional software engineering environments.

The programme focuses on developing engineers who are not only technically capable, but adaptable, resilient, and prepared for the realities of modern software careers.

My Role

Professional Development Coach & Technical Mentor

I worked directly with Codam students as part of a structured mentorship programme.

Responsibilities included:

Delivering a professional development workshop

Mentoring two students across distinct technical tracks

Sharing industry experience from both technical and business perspectives

Coaching students on career strategy, positioning, and growth

Supporting students in translating academic projects into professional narratives

One mentee focused on web application development (JavaScript, React.js, Node.js), while the other focused on Python and Artificial Intelligence.
*/
